import { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const components = {
  a: ({ node, children, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="underline break-words"
    >
      {children}
    </a>
  ),
  p: ({ node, ...props }) => (
    <p {...props} className="m-0 mb-2 last:mb-0 leading-relaxed" />
  ),
  ul: ({ node, ordered, ...props }) => (
    <ul
      {...props}
      className="list-disc ps-5 mb-2 last:mb-0 space-y-0.5 marker:opacity-70"
    />
  ),
  ol: ({ node, ordered, ...props }) => (
    <ol
      {...props}
      className="list-decimal ps-5 mb-2 last:mb-0 space-y-0.5 marker:opacity-70"
    />
  ),
  li: ({ node, ...props }) => <li {...props} className="leading-snug" />,
  h1: ({ node, children, ...props }) => (
    <h1 {...props} className="text-[18px] font-semibold mb-2 mt-1">
      {children}
    </h1>
  ),
  h2: ({ node, children, ...props }) => (
    <h2 {...props} className="text-[16px] font-semibold mb-2 mt-1">
      {children}
    </h2>
  ),
  h3: ({ node, children, ...props }) => (
    <h3 {...props} className="text-[15px] font-semibold mb-1.5 mt-1">
      {children}
    </h3>
  ),
  h4: ({ node, children, ...props }) => (
    <h4 {...props} className="text-[14px] font-semibold mb-1 mt-1">
      {children}
    </h4>
  ),
  code: ({ node, className, children, ...props }) => {
    const isBlock = /language-/.test(className || "");
    if (isBlock) {
      return (
        <code
          {...props}
          className={`block font-mono text-[12.5px] leading-relaxed ${className || ""}`}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        {...props}
        className="px-1 py-[1px] rounded bg-black/10 text-[0.9em] font-mono"
      >
        {children}
      </code>
    );
  },
  pre: ({ node, ...props }) => (
    <pre
      {...props}
      className="bg-black/10 rounded-md p-2.5 my-2 overflow-x-auto"
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      {...props}
      className="border-s-2 border-current/30 ps-3 italic opacity-90 my-2"
    />
  ),
  hr: ({ node, ...props }) => (
    <hr {...props} className="my-3 border-current/20" />
  ),
  strong: ({ node, ...props }) => (
    <strong {...props} className="font-semibold" />
  ),
  em: ({ node, ...props }) => <em {...props} className="italic" />,
  img: ({ node, ...props }) => (
    <img
      {...props}
      alt={props.alt || ""}
      className="max-w-full rounded-md my-2"
    />
  ),
};

const normalizeContent = (raw) => {
  if (typeof raw !== "string") return "";
  // Convert lone newlines into hard line breaks so chat-style messages preserve
  // their visual line wrapping without forcing every line into a paragraph.
  return raw.replace(/(?<!\n)\n(?!\n)/g, "  \n");
};

const MessageContent = ({ content, className = "" }) => {
  const text = useMemo(() => normalizeContent(content), [content]);

  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default memo(MessageContent);
